"use client";

import { useRef } from "react";
import Form from "@/HomeFeatures/Contact/Form";
import CareerForm from "@/HomeFeatures/CareerForm";
import { Images } from "@/app/assets/shared";
import Image from "next/image";
import Link from "next/link";

interface ContactSectionProps {
  variant?: "default" | "alt";
  formType?: "contact" | "career";
}

export default function ContactSection({
  variant = "default",
  formType = "contact",
}: ContactSectionProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  return (
    <section
      ref={sectionRef}
      className="contact bg-[#D9E2ED] overflow-hidden max-h-[max-content]"
    >
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] pt-[30px] lg:pt-[100px] pb-[50px] lg:pb-[100px] relative z-1">
        <div className="contact-container flex flex-col lg:flex-row justify-between min-h-[700px] gap-[30px] 2xl:gap-[0px]">
          <div className="contact-left flex flex-col gap-[40px] lg:gap-[20px] flex-1 justify-between">
            {variant === "default" ? (
              <>
                <div className="parent_wrap">
                  <h2 className="self-stretch justify-start max-w-[90%] xl:max-w-[22ch] text-[#00040b] clash-font font-medium leading-[1.2] tracking-wider text-[clamp(1.25rem,0.2945rem+2.0115vw,3.4375rem)]">
                    We Just Need Some Basic Information, And We’ll Take{" "}
                    <span className="text-[#1D4ED8]">It From There.</span>
                  </h2>
                  <p
                    className="max-w-[65ch] justify-start text-[#00040B] font-medium leading-[1.6] tracking-wide"
                    style={{
                      fontSize: "clamp(16px, 2vw, 20px)",
                    }}
                  >
                    We'll schedule a call to discuss your idea. After discovery
                    sessions, we'll send a proposal, and upon approval, we'll
                    get started.
                  </p>
                </div>

                <div className="xl:mt-[10%] grid gap-[2px] w-full max-w-[640px]">
                  {/* Contact Methods */}
                  <div className="w-full flex items-start mb-5">
                    <div className="w-[54px] h-[54px] bg-white flex items-center justify-center flex-shrink-0 gap-1 px-1">
                      <Image
                        src={Images?.contactUs.mail as any}
                        alt="mail"
                        width={20}
                        height={20}
                        className="w-[20px] h-[20px]"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                    <div className="ml-[16px] flex flex-col justify-center min-h-[54px]">
                      <div className="text-xs uppercase tracking-wide text-[#00040b]/80 font-semibold mb-1">
                        If Not Forms, Brief Us@
                      </div>
                      <div className="flex flex-col">
                        <a
                          href="mailto:mail@konstantinfo.com"
                          className="text-[#00040b] leading-tight"
                          style={{ fontSize: "clamp(16px, 2vw, 20px)" }}
                        >
                          mail@konstantinfo.com
                        </a>
                        <a
                          href="tel:+1-310-933-5465"
                          className="text-[#00040b] leading-tight mt-1"
                          style={{ fontSize: "clamp(16px, 2vw, 20px)" }}
                        >
                          +1-310-933-5465
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Careers */}
                  <div className="w-full flex items-start mb-5">
                    <div className="w-[54px] h-[54px] bg-white flex items-center justify-center flex-shrink-0 gap-1 px-1">
                      <Image
                        src={Images?.contactUs.phone as any}
                        alt="phone"
                        width={20}
                        height={20}
                        className="w-[20px] h-[20px]"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                    <div className="ml-[16px] flex flex-col justify-center min-h-[54px]">
                      <div className="text-xs uppercase tracking-wide text-[#00040b]/80 font-semibold mb-1">
                        Be A Part Of Our Team
                      </div>
                      <div className="flex flex-col">
                        <a
                          href="mailto:career@konstantinfo.com"
                          className="text-[#00040b] leading-tight"
                          style={{ fontSize: "clamp(16px, 2vw, 20px)" }}
                        >
                          career@konstantinfo.com
                        </a>
                        <div
                          className="text-[#00040b] leading-tight mt-1"
                          style={{ fontSize: "clamp(16px, 2vw, 20px)" }}
                        >
                          <a href="tel:+91-141-2291398">+91-141-2291398</a>,{" "}
                          <a href="tel:+91-141-4028078">4028078</a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MS Teams */}
                  <div className="w-full flex items-start mb-5">
                    <div className="w-[54px] h-[54px] bg-white flex items-center justify-center flex-shrink-0">
                      <Image
                        src={Images?.contactUs.team as any}
                        alt="ms team"
                        width={24}
                        height={24}
                        className="w-[24px] h-[24px]"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                    <div className="ml-[16px] flex flex-col justify-center h-[54px]">
                      <div className="text-xs uppercase tracking-wide text-[#00040b]/80 font-semibold mb-1">
                        Talk To Us On MS Team
                      </div>
                      <Link
                        href={
                          "https://teams.microsoft.com/l/chat/0/0?users=mail@konstantinfosolutions.com"
                        }
                        className="text-[#00040b] leading-tight"
                        style={{ fontSize: "clamp(16px, 2vw, 22px)" }}
                      >
                        Connect on MS Teams
                      </Link>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <h2 className="self-stretch justify-start lg:max-w-[22ch] max-w-full text-[#00040b] clash-font font-medium leading-[1.24] tracking-wider text-[length:var(--section-title)]">
                    Have A Project Idea
                    <br className="hidden lg:block" />
                    In Mind?{" "}
                    <span className="text-[#1D4ED8]">Let's Get Started</span>
                  </h2>
                  <p
                    className="max-w-[65ch] justify-start text-[#00040B] font-medium leading-[1.6] tracking-wide"
                    style={{
                      fontSize: "clamp(16px, 2vw, 20px)",
                    }}
                  >
                    We'll schedule a call to discuss your idea. After discovery
                    sessions, we'll send a proposal, and upon approval, we'll
                    get started.
                  </p>
                </div>

                <div className="lg:mt-[10%] grid lg:gap-[5px] gap-[10px] w-full max-w-[640px]">
                  <div className="w-full flex items-start mb-5">
                    <div className="w-[54px] h-[54px] bg-white flex items-center justify-center flex-shrink-0">
                      <Image
                        src={Images?.contactUs.mail as any}
                        alt="mail"
                        width={24}
                        height={24}
                        className="w-[24px] h-[24px]"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                    <div className="ml-[16px] flex flex-col justify-center h-[54px]">
                      <div className="text-xs uppercase tracking-wide text-[#00040b]/80 font-semibold mb-1">
                        Mail to our sales department
                      </div>
                      <a
                        href="mailto:sales@konstantinfo.com"
                        className="text-[#00040b] leading-tight"
                        style={{ fontSize: "clamp(16px, 2vw, 22px)" }}
                      >
                        sales@konstantinfo.com
                      </a>
                    </div>
                  </div>

                  <div className="w-full pb-2">
                    <div className="flex items-start">
                      <div className="w-[54px] h-[54px] bg-white flex items-center justify-center flex-shrink-0">
                        <Image
                          src={Images?.contactUs.team as any}
                          alt="ms team"
                          width={24}
                          height={24}
                          className="w-[24px] h-[24px]"
                          sizes="(max-width: 1024px) 100vw, 50vw"
                        />
                      </div>
                      <div className="ml-[16px] flex flex-col justify-center h-[54px]">
                        <div className="text-xs uppercase tracking-wide text-[#00040b]/80 font-semibold mb-1">
                          MS Teams
                        </div>
                        <Link
                          href={
                            "https://teams.microsoft.com/l/chat/0/0?users=mail@konstantinfosolutions.com"
                          }
                          className="text-[#00040b] leading-tight"
                          style={{ fontSize: "clamp(16px, 2vw, 22px)" }}
                        >
                          Connect on MS Teams
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex-1 h-[max-content] my-auto" id="contact-main">
            {formType == "contact" ? <Form /> : <CareerForm />}
          </div>
        </div>
      </div>
    </section>
  );
}
